/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MOBILE_TABLET_MIGRATION_PHASE5
 * Date:         2026-02-09
 * Modified By:  Claude
 *
 * Description:
 * Created reusable FilterDrawer component for mobile filter sidebars.
 * Slides from right on mobile, renders inline on desktop.
 *
 * File Size Check:
 * - Current: NEW FILE
 * - Projected: ~140 lines
 * - Decision: Proceed (within component limit)
 *
 * Related Screens / Modules:
 * - client/src/components/ui/FilterDrawer.tsx (NEW)
 * - client/src/styles/components/_filter-drawer.scss (NEW)
 */

import React, { useEffect, useRef } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  onApply?: () => void;
  onClear?: () => void;
  applyLabel?: string;
  clearLabel?: string;
  showFooter?: boolean;
  activeFilterCount?: number;
}

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  title = "Filters",
  children,
  onApply,
  onClear,
  applyLabel = "Apply",
  clearLabel = "Clear All",
  showFooter = true,
  activeFilterCount = 0,
}) => {
  const { isMobile, isTablet } = useMediaQuery();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Handle body scroll lock when drawer is open on mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Handle click outside to close on mobile
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isMobile &&
        isOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile, isOpen, onClose]);

  // On desktop/tablet, always render inline (no drawer behavior)
  if (!isMobile && !isTablet) {
    return (
      <aside className="filter-drawer filter-drawer--inline">
        <div className="filter-drawer__content">{children}</div>
        {showFooter && (onApply || onClear) && (
          <div className="filter-drawer__footer filter-drawer__footer--inline">
            {onClear && (
              <button
                type="button"
                className="btn-figma-1"
                onClick={onClear}
              >
                {clearLabel}
              </button>
            )}
            {onApply && (
              <button
                type="button"
                className="btn-primary"
                onClick={onApply}
              >
                {applyLabel}
              </button>
            )}
          </div>
        )}
      </aside>
    );
  }

  // Mobile/Tablet: Slide-out drawer
  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="filter-drawer__overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <aside
        ref={drawerRef}
        className={`filter-drawer filter-drawer--mobile ${isOpen ? "open" : ""}`}
        aria-label={title}
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="filter-drawer__header">
          <h2 className="filter-drawer__title u-H3-22">
            {title}
            {activeFilterCount > 0 && (
              <span className="filter-drawer__count">({activeFilterCount})</span>
            )}
          </h2>
          <button
            type="button"
            className="filter-drawer__close touch-target"
            onClick={onClose}
            aria-label="Close filters"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="filter-drawer__content">{children}</div>

        {/* Footer */}
        {showFooter && (onApply || onClear) && (
          <div className="filter-drawer__footer">
            {onClear && (
              <button
                type="button"
                className="btn-figma-1 filter-drawer__btn"
                onClick={onClear}
              >
                {clearLabel}
              </button>
            )}
            {onApply && (
              <button
                type="button"
                className="btn-primary filter-drawer__btn"
                onClick={() => {
                  onApply();
                  onClose();
                }}
              >
                {applyLabel}
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

export default FilterDrawer;
